import DrawingBase from '../drawing.base'
import { scaledCoordinate } from '../drawing.helper'
import { utcToIST } from '../../../../util/dateTime'
import { binarySearchFirstDuplicate } from '../../../../util/general'
import { DrawingOptions, DrawingSettingGroup, DrawingTypes, SettingValues } from '../drawing.interface'
import { ChartEvents } from '../../chart.events'
import { MouseEventParams, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import { CompanyEventProto, PurposeType } from '../../../../proto/generated/company_event'
import { getNextValidTradingDay } from '../../../../util/tradingCalendar'
import {
    getCompanyEventColor,
    getCompanyEventLetter,
    getCompanyEventPurposeDisplayName,
} from './companyEventTool.helpers'
import type { CompanyEventDrawingSettings } from './companyEventTool.types'
export type { CompanyEventDrawingSettings } from './companyEventTool.types'

export type CompanyEventType = 'companyEvent'

export type CompanyEventState = {
    data: Record<number, CompanyEventProto[]>
}

export type CompanyEventOptions = {
    drawingType: CompanyEventType
}

class CompanyEventTool extends DrawingBase<CompanyEventOptions, CompanyEventDrawingSettings, CompanyEventState> {
    static getEventLetter = getCompanyEventLetter
    static getColorForEvent = getCompanyEventColor
    static getPurposeDisplayName = getCompanyEventPurposeDisplayName

    initialPoint = 0

    private findHoveredData(x: number, y: number): CompanyEventProto[] | null {
        const sx = scaledCoordinate(x, this.horizontalPixelRatio)
        const sy = scaledCoordinate(y, this.verticalPixelRatio)
        const data = this._state.data

        if (!data || typeof data !== 'object') {
            console.warn('Data', data)
            return null
        }

        const circleRadius = 10 * window.devicePixelRatio
        const marginBottom = 5 * window.devicePixelRatio
        const canvasHeight = this.canvasHeight - circleRadius - marginBottom
        const primaryData = this._chart.getRenderedPrimaryData()
        const futureData = this._chart.emptyTimeSlots
        const combinedData = [...primaryData, ...futureData]
            .filter((item) => item && item.time)
            .sort((a, b) => (a.time as number) - (b.time as number))

        if (!combinedData) return null

        let hoveredActions: CompanyEventProto[] = []

        for (const timestamp in data) {
            const groupedActions = data[timestamp]

            if (Array.isArray(groupedActions) && groupedActions.length > 0) {
                const timestampInMs = new Date(groupedActions[0].date).getTime()
                const istTimestampInMs = utcToIST(timestampInMs / 1000) * 1000
                const dt = new Date(istTimestampInMs)
                dt.setHours(0, 0, 0, 0)

                const timestamps = combinedData.map((candle) => {
                    const dt = new Date((candle.time as number) * 1000)
                    dt.setHours(0, 0, 0, 0)
                    return dt.getTime()
                })

                const index = binarySearchFirstDuplicate(timestamps, dt.getTime())
                if (index === -1) continue

                const xCoordinate = scaledCoordinate(
                    this._chartApi.timeScale().timeToCoordinate(combinedData[index].time) as number,
                    this.horizontalPixelRatio
                )

                if (!xCoordinate) continue

                groupedActions.forEach((action, i) => {
                    const yCoordinate = canvasHeight - i * (circleRadius + 30)
                    const dx = sx - xCoordinate
                    const dy = sy - yCoordinate
                    const distance = Math.sqrt(dx * dx + dy * dy)

                    if (distance <= circleRadius) {
                        hoveredActions.push(action)
                    }
                })
            }
        }

        return hoveredActions.length > 0 ? hoveredActions : null
    }


    private clickedCircles: string | null

    constructor(options: DrawingOptions<any, any, any>) {
        super(options)
        this.clickedCircles = null
        this._chartApi.subscribeClick(this.handleChartClick.bind(this))
        this._chartApi.timeScale().subscribeVisibleTimeRangeChange(() => {
            this._chart.publishEvent(ChartEvents.COMPANY_EVENT_CLICKED, null)
            this.clickedCircles = null
        })
    }

    private handleChartClick(param: MouseEventParams): void {
        const { point } = param
        if (!point) return

        const clickedData = this.findHoveredData(point.x, point.y)
        if (clickedData && clickedData.length > 0) {
            let clickedEvent = clickedData[0]
            if (clickedData.length > 1) {
                const lastClickedIndex = clickedData.findIndex(event =>
                    this.clickedCircles === `${event.date}-${event.purpose}-${event.details}`
                )
                clickedEvent = clickedData[(lastClickedIndex + 1) % clickedData.length] // Cycle to next event
            }

            const actionKey = `${clickedEvent.date}-${clickedEvent.purpose}-${clickedEvent.details}`

            if (this.clickedCircles === actionKey) {
                this.clickedCircles = null
                this._chart.publishEvent(ChartEvents.COMPANY_EVENT_CLICKED, null)
            } else {
                this.clickedCircles = actionKey
                this._chart.publishEvent(ChartEvents.COMPANY_EVENT_CLICKED, {
                    x: point.x,
                    y: point.y - 10,
                    date: clickedEvent.date,
                    purpose: clickedEvent.purpose,
                    type: this.getDrawingType(),
                    details: clickedEvent.details
                })
            }
        } else {
            this._chart.publishEvent(ChartEvents.COMPANY_EVENT_CLICKED, null)
            this.clickedCircles = null
        }
    }

    normalizeCompanyEvents(data: any) {
        const updated: any = {}
        for (const key in data) {
            const group = data[key]
            for (const event of group) {
                const originalDate = new Date(event.date)
                const tradingDate = getNextValidTradingDay(originalDate, this._chart.tradingCalendarData)
                if (!tradingDate) continue

                const timestamp = tradingDate.getTime()
                const updatedEvent = {
                    ...event,
                    date: tradingDate.toLocaleDateString('en-CA')
                }
                if (!updated[timestamp]) {
                    updated[timestamp] = []
                }
                updated[timestamp].push(updatedEvent)
            }
        }

        return updated
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        const { data } = this._state
        if (!data) return
        this._state.data = this.normalizeCompanyEvents(this._state.data)
        const visibleLogicalRange = this._chartApi.timeScale().getVisibleLogicalRange()
        if (!visibleLogicalRange) return

        const barsInfo = this.getSeries().barsInLogicalRange(visibleLogicalRange)
        if (!barsInfo) return

        const devicePixelRatio = window.devicePixelRatio || 1
        const baseSize = 10
        const circleRadius = baseSize * devicePixelRatio
        const marginBottom = 5 * devicePixelRatio
        const canvasHeight = this.canvasHeight - circleRadius - marginBottom
        const adjustedFontSize = baseSize * devicePixelRatio

        Object.keys(data).forEach((key) => {
            const actions = data[Number(key)]
            if (Array.isArray(actions) && actions.length === 0) return
            const primaryData = this._chart.getRenderedPrimaryData()
            const futureData = this._chart.emptyTimeSlots
            const combinedData = [...primaryData, ...futureData]
                .filter((item) => item && item.time)
                .sort((a, b) => (a.time as number) - (b.time as number))
            const index = binarySearchFirstDuplicate(
                combinedData.map((candle) => {
                    const dt = new Date((candle.time as number) * 1000)
                    dt.setHours(0, 0, 0, 0)
                    return dt.getTime()
                }),
                Number(key)
            )
            if (index === -1) return

            const xCoordinate = scaledCoordinate(
                this._chartApi.timeScale().timeToCoordinate(combinedData[index].time) as number,
                this.horizontalPixelRatio
            )

            if (!xCoordinate) return

            actions.forEach((action: CompanyEventProto, i: any) => {
                const actionKey = `${action.date}-${action.purpose}-${action.details}`
                const yCoordinate = canvasHeight - i * (circleRadius + 30)
                ctx.save()
                ctx.translate(xCoordinate, yCoordinate)

                ctx.beginPath()
                ctx.arc(0, 0, circleRadius + 2, 0, Math.PI * 2)
                ctx.strokeStyle = this._chart.theme.palette.background.paper
                ctx.lineWidth = 2
                ctx.stroke()
                ctx.closePath()

                ctx.beginPath()
                ctx.arc(0, 0, circleRadius, 0, Math.PI * 2)
                ctx.strokeStyle = CompanyEventTool.getColorForEvent(action.purpose.map((code: number) => CompanyEventTool.getPurposeDisplayName(code).toLowerCase()))
                ctx.lineWidth = 3
                ctx.stroke()
                ctx.closePath()

                ctx.beginPath()
                ctx.arc(0, 0, circleRadius - 2, 0, Math.PI * 2)
                ctx.fillStyle = this.clickedCircles === actionKey
                    ? CompanyEventTool.getColorForEvent(action.purpose.map((code: number) => CompanyEventTool.getPurposeDisplayName(code).toLowerCase()))
                    : this._chart.theme.palette.background.paper
                ctx.fill()
                ctx.closePath()

                ctx.fillStyle = this.clickedCircles === actionKey
                    ? 'white'
                    : CompanyEventTool.getColorForEvent(action.purpose.map((code: number) => CompanyEventTool.getPurposeDisplayName(code).toLowerCase()))
                ctx.font = `bold ${adjustedFontSize}px Arial`
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.fillText('E', 0, 0)
                ctx.restore()
            })
        })
    }


    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const hoveredData = this.findHoveredData(x, y)
        if (hoveredData && hoveredData.length > 0) {
            this._hovered = true

            const primaryData = this._chart.getRenderedPrimaryData()
            const futureData = this._chart.emptyTimeSlots
            const combinedData = [...primaryData, ...futureData]
                .filter((item) => item && item.time)
                .sort((a, b) => (a.time as number) - (b.time as number))

            const targetCandle = combinedData[this.initialPoint]
            const timestamp = (targetCandle.time as number) * 1000

            this._chart.setCrossHairPosition(targetCandle.customValues, timestamp)
            const colors = hoveredData.map(event =>
                CompanyEventTool.getColorForEvent(event.purpose.map(code => PurposeType[code]))
            )
            const highlightColor = colors[0]

            this._chartApi.applyOptions({
                crosshair: {
                    vertLine: {
                        color: highlightColor,
                        visible: true
                    },
                    horzLine: {
                        visible: false
                    }
                }
            })

            return {
                externalId: this.id,
                zOrder: 'top',
                cursorStyle: 'pointer'
            }
        } else if (this._hovered) {
            this._hovered = false

            this._chartApi.applyOptions({
                crosshair: {
                    vertLine: {
                        color: '#758696',
                        visible: true
                    },
                    horzLine: {
                        visible: true
                    }
                }
            })
        }

        return null
    }

    updateHandle = (id: string, time: Time, price: number) => {

    }

    onSettingsUpdate = (settings: Partial<CompanyEventDrawingSettings>) => {
        if (settings.data) {
            this._state.data = settings.data
        }
        this._settings = {
            ...this._settings,
            ...settings
        }
        this.primitive?.requestUpdate()
    }

    getAvailableSettings = (): DrawingSettingGroup<CompanyEventDrawingSettings, SettingValues>[] => {
        return []
    }

    getDrawingType(): DrawingTypes {
        switch (this.options.drawingType) {
            case 'companyEvent':
                return 'companyEvent'
        }
    }

    getSettingsValues(): CompanyEventDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        return []
    }
}

export default CompanyEventTool